import { createPath, type Path, parsePath, type To } from 'react-router-dom'
import {
  appendOrganizationParam,
  getUrlOrganizationId,
  readOrganizationParam,
  TAB_ORGANIZATION_QUERY_PARAM,
} from './tab-organization'

const EXTERNAL_URL_PATTERN = /^(?:[a-z][a-z\d+.-]*:|\/\/)/i

function isExternalUrl(path: string): boolean {
  return EXTERNAL_URL_PATTERN.test(path)
}

/**
 * Adds `?org=` to an in-app destination. An explicit `org` already in `to` wins, and the other
 * params and the hash are kept. A string `to` comes back as a partial path so relative links keep
 * resolving against the current route.
 */
export function withTabOrganization(to: To, organizationId: string | null): To {
  if (organizationId === null) return to
  if (typeof to === 'string' && isExternalUrl(to)) return to
  const path: Partial<Path> = typeof to === 'string' ? parsePath(to) : to
  if (readOrganizationParam(path.search ?? '') !== null) return to
  return { ...path, search: appendOrganizationParam(path.search, organizationId) }
}

export function orgHrefFor(path: string, organizationId: string | null): string {
  const to = withTabOrganization(path, organizationId)
  return typeof to === 'string' ? to : createPath({ pathname: '', ...to })
}

/** For `window.open`, `location.href` and plain `href`s outside React render. */
export function orgHref(path: string): string {
  return orgHrefFor(path, getUrlOrganizationId())
}

/** For code that rebuilds the whole query string: carries the current `org` over. */
export function keepOrganizationParam(
  current: URLSearchParams,
  next: URLSearchParams,
): URLSearchParams {
  const organizationId = readOrganizationParam(current.toString())
  if (organizationId !== null) next.set(TAB_ORGANIZATION_QUERY_PARAM, organizationId)
  return next
}
